import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline'
import { Factory, Package, TrendingUp, TrendingDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { giacenzeProdottiApi, giacenzeComponentiApi, lottiProduzioneApi, movimentiApi } from '@/api/magazzino'
import InventoryFlowReport from './InventoryFlowReport'

/**
 * Dashboard Magazzino con riepilogo giacenze e alert.
 */
export default function DashboardMagazzino() {
  // Query giacenze prodotti
  const { data: giacenzeProdottiData } = useQuery({
    queryKey: ['giacenze-prodotti'],
    queryFn: () => giacenzeProdottiApi.getAll(),
  })

  // Query prodotti sottoscorta
  const { data: sottoscortaProdottiData } = useQuery({
    queryKey: ['giacenze-prodotti-sottoscorta'],
    queryFn: () => giacenzeProdottiApi.sottoscorta(),
  })

  // Query giacenze componenti
  const { data: giacenzeComponentiData } = useQuery({
    queryKey: ['giacenze-componenti'],
    queryFn: () => giacenzeComponentiApi.getAll(),
  })

  // Query componenti sottoscorta
  const { data: sottoscortaComponentiData } = useQuery({
    queryKey: ['giacenze-componenti-sottoscorta'],
    queryFn: () => giacenzeComponentiApi.sottoscorta(),
  })

  // Query ultimi movimenti
  const { data: movimentiData } = useQuery({
    queryKey: ['movimenti-recenti'],
    queryFn: () => movimentiApi.getAll({ limit: 5 }),
  })

  // Query ultimi lotti assemblaggio
  const { data: lottiRecentiData } = useQuery({
    queryKey: ['lotti-recenti'],
    queryFn: () => lottiProduzioneApi.getAll({ ordering: '-data_produzione' }),
  })

  const giacenzeProdotti = Array.isArray(giacenzeProdottiData?.data)
    ? giacenzeProdottiData.data
    : (giacenzeProdottiData?.data?.results || [])

  const sottoscortaProdotti = Array.isArray(sottoscortaProdottiData?.data)
    ? sottoscortaProdottiData.data
    : (sottoscortaProdottiData?.data?.results || [])

  const giacenzeComponenti = Array.isArray(giacenzeComponentiData?.data)
    ? giacenzeComponentiData.data
    : (giacenzeComponentiData?.data?.results || [])

  const sottoscortaComponenti = Array.isArray(sottoscortaComponentiData?.data)
    ? sottoscortaComponentiData.data
    : (sottoscortaComponentiData?.data?.results || [])

  const movimenti = Array.isArray(movimentiData?.data)
    ? movimentiData.data
    : (movimentiData?.data?.results || [])
  const lottiRecenti = Array.isArray(lottiRecentiData?.data)
    ? lottiRecentiData.data
    : (lottiRecentiData?.data?.results || [])

  const totaleProdotti = typeof giacenzeProdottiData?.data?.count === 'number'
    ? giacenzeProdottiData.data.count
    : giacenzeProdotti.length
  const totaleComponenti = typeof giacenzeComponentiData?.data?.count === 'number'
    ? giacenzeComponentiData.data.count
    : giacenzeComponenti.length
  const numSottoscortaProdotti = sottoscortaProdotti.length
  const numSottoscortaComponenti = sottoscortaComponenti.length

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getDeltaScorta = (movimento) => {
    if (movimento.impatto_scorta === 'riservata') {
      if (movimento.tipo === 'riserva') return Math.abs(movimento.quantita)
      if (movimento.tipo === 'ripristino') return -Math.abs(movimento.quantita)
    }
    return movimento.quantita
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard Magazzino</h1>
        <p className="text-muted-foreground">
          Riepilogo giacenze, alert sottoscorta e ultimi movimenti
        </p>
      </div>

      {/* Cards Riepilogo */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Giacenze Prodotti */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Giacenze Prodotti</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totaleProdotti}</div>
            <p className="text-xs text-muted-foreground">
              {numSottoscortaProdotti > 0 && (
                <span className="text-red-600 font-semibold">
                  {numSottoscortaProdotti} sottoscorta
                </span>
              )}
              {numSottoscortaProdotti === 0 && 'Tutte OK'}
            </p>
          </CardContent>
        </Card>

        {/* Giacenze Componenti */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Giacenze Componenti</CardTitle>
            <ArchiveBoxIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totaleComponenti}</div>
            <p className="text-xs text-muted-foreground">
              {numSottoscortaComponenti > 0 && (
                <span className="text-red-600 font-semibold">
                  {numSottoscortaComponenti} sottoscorta
                </span>
              )}
              {numSottoscortaComponenti === 0 && 'Tutte OK'}
            </p>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Carico Merce</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full" size="sm">
              <Link to="/magazzino/carico">Registra Carico</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scarico Merce</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full" size="sm" variant="outline">
              <Link to="/magazzino/scarico">Registra Scarico</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <InventoryFlowReport />

      {/* Alert Sottoscorta */}
      {(numSottoscortaProdotti > 0 || numSottoscortaComponenti > 0) && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <ExclamationTriangleIcon className="h-5 w-5" />
              Alert Sottoscorta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {numSottoscortaProdotti > 0 && (
              <div>
                <p className="font-semibold text-red-900 mb-2">
                  {numSottoscortaProdotti} Prodott{numSottoscortaProdotti === 1 ? 'o' : 'i'} Sottoscorta
                </p>
                <div className="space-y-1">
                  {sottoscortaProdotti.slice(0, 5).map((g) => (
                    <div key={g.id} className="flex items-center justify-between text-sm">
                      <span>{g.prodotto_nome}</span>
                      <Badge variant="destructive">
                        {g.quantita_effettiva} / {g.soglia_minima}
                      </Badge>
                    </div>
                  ))}
                </div>
                {numSottoscortaProdotti > 5 && (
                  <Button asChild variant="link" size="sm" className="mt-2 text-red-700">
                    <Link to="/prodotti">
                      Vedi tutti ({numSottoscortaProdotti})
                    </Link>
                  </Button>
                )}
              </div>
            )}

            {numSottoscortaComponenti > 0 && (
              <div>
                <p className="font-semibold text-red-900 mb-2">
                  {numSottoscortaComponenti} Component{numSottoscortaComponenti === 1 ? 'e' : 'i'} Sottoscorta
                </p>
                <div className="space-y-1">
                  {sottoscortaComponenti.slice(0, 5).map((g) => (
                    <div key={g.id} className="flex items-center justify-between text-sm">
                      <span>{g.componente_nome} ({g.componente_tipo})</span>
                      <Badge variant="destructive">
                        {g.quantita_effettiva} / {g.soglia_minima}
                      </Badge>
                    </div>
                  ))}
                </div>
                {numSottoscortaComponenti > 5 && (
                  <Button asChild variant="link" size="sm" className="mt-2 text-red-700">
                    <Link to="/box/componenti">
                      Vedi tutti ({numSottoscortaComponenti})
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sintesi operativa */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ultimi Eventi Magazzino</CardTitle>
          </CardHeader>
          <CardContent>
            {movimenti.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">Nessun movimento recente</p>
            ) : (
              <div className="space-y-2">
                {movimenti.slice(0, 5).map((movimento) => {
                  const delta = getDeltaScorta(movimento)
                  return (
                    <div
                      key={movimento.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant={delta >= 0 ? 'success' : 'destructive'}>
                            {movimento.tipo_display}
                          </Badge>
                          <span className="text-sm font-medium truncate">{movimento.item_nome}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(movimento.created_at)}
                        </p>
                      </div>
                      <span className={delta >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {delta > 0 ? '+' : ''}{delta}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/magazzino/movimenti">Apri Storico Completo</Link>
              </Button>
              <Button asChild size="sm" className="w-full">
                <Link to="/box/tipologie/assembla">Assembla Box</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              Ultimi Lotti Assemblaggio
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lottiRecenti.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">Nessun lotto recente</p>
            ) : (
              <div className="space-y-2">
                {lottiRecenti.slice(0, 5).map((lotto) => (
                  <div
                    key={lotto.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{lotto.tipologia_nome}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {lotto.codice} • {formatDate(lotto.data_produzione)}
                      </p>
                    </div>
                    <Badge variant="secondary">{lotto.quantita} pz</Badge>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/box/tipologie">Vai a Tipologie Box</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
